import { memo } from 'react';
import { Grid, MenuItem } from '@mui/material';
import { FastField, withFormik } from 'formik';
import * as Yup from 'yup';

import PersianDatePicker from '../../../../components/formik/date-picker/date-picker';
import { TextField } from '../../../../components/formik/text-field';
import {
    isNotInFuture,
    isValidNumbers,
    isValidPersianAlpha,
} from 'utils/custom-validation';

const LegalSchema = Yup.object().shape({
    name: Yup.string()
        .matches(isValidPersianAlpha, 'فقط حروف فارسی مجاز است')
        .required('وارد کردن نام شخصیت حقوقی الزامی است')
        .min(3, 'فیلد نام شخصیت حقوقی باید حداقل 3 کاراکتر باشد')
        .max(64, 'فیلد نام شخصیت حقوقی باید حداکثر 64 کاراکتر باشد')
        .test(
            'no-whitespace-only',
            'فیلد نام شخصیت حقوقی نمی‌تواند فقط شامل فاصله باشد',
            (value: any) => value.trim().length > 0,
        ),

    passport: Yup.string()
        .required('وارد کردن شناسه ملی الزامی است')
        .max(15, 'فیلد شناسه ملی باید حداکثر 15 کاراکتر باشد')
        .matches(isValidNumbers, 'فیلد شناسه ملی باید فقط شامل اعداد باشد'),

    national: Yup.string()
        .required('وارد کردن شماره ثبت الزامی است')
        .max(15, 'فیلد شماره ثبت باید حداکثر 15 کاراکتر باشد')
        .matches(isValidNumbers, 'فیلد شماره ثبت باید فقط شامل اعداد باشد'),

    birth: Yup.string()
        .required('وارد کردن تاریخ ثبت الزامی است')
        .max(64, 'فیلد تاریخ ثبت باید حداکثر 64 کاراکتر باشد')
        .test(
            'is-not-in-future',
            'تاریخ انتخاب شده نباید بعد از امروز باشد',
            isNotInFuture,
        ),

    company_type: Yup.string().required('نوع شرکت الزامی است'),
});

const LegalFields: Array<any> = [
    {
        name: 'name',
        label: 'نام شخصیت حقوقی',
        icon: <i className="fa-light fa-user" />,
        component: TextField,
        maxLength: 65,
    },
    {
        name: 'company_type',
        label: 'نوع شرکت',
        options: [
            {
                label: 'سهامی عام',
                value: 'public',
            },
            {
                label: 'مسئولیت محدود',
                value: 'limited',
            },
            {
                label: 'تضامنی',
                value: 'solidarity',
            },
            {
                label: 'مختلط غیرسهامی',
                value: 'non-stock-mixed',
            },
            {
                label: 'مختلط سهامی',
                value: 'mixed-stock',
            },
            {
                label: 'نسبی',
                value: 'relative',
            },
            {
                label: 'تعاونی',
                value: 'cooperative',
            },
            {
                label: 'موسسه',
                value: 'institute',
            },
        ],
        component: TextField,
        icon: <i className="fa-light fa-building" />,
    },
    {
        name: 'passport',
        label: 'شناسه ملی',
        icon: <i className="fa-light fa-id-card" />,
        ltr: 'true',
        component: TextField,
        numericOnly: true,
        maxLength: 15,
    },
    {
        name: 'national',
        label: 'شماره ثبت',
        icon: <i className="fa-light fa-passport" />,
        ltr: 'true',
        component: TextField,
        numericOnly: true,
        maxLength: 15,
    },
    {
        name: 'birth',
        label: 'تاریخ ثبت',
        icon: <i className="fa-light fa-calendar-days" />,
        component: PersianDatePicker,
        minDate: '1200/01/01',
        // Doran defaults to ±30 years. 330 gives ±165 years, so from 1405
        // both 1250 and the older 1240 use case are available in the same year panel.
        yearSpan: 330,
    },
];

const LegalFormTemplate = memo((props: any): JSX.Element => {
    const disableAutoFill = props?.disableAutoFill ?? false;
    const initialBirthPickerView = props?.initialBirthPickerView;

    return (
        <Grid container spacing={2}>
            {LegalFields.map((field) => {
                const datePickerProps =
                    field.name === 'birth' && initialBirthPickerView
                        ? { initialPickerView: initialBirthPickerView }
                        : {};

                return (
                    <Grid item xs={12} md={6} key={field.name}>
                        <FastField
                            component={field.component}
                            select={field?.options ? true : false}
                            {...field}
                            {...datePickerProps}
                            disableAutoFill={disableAutoFill}>
                            {field.options?.map((menu: any) => (
                                <MenuItem value={menu.value} key={menu.value}>
                                    {menu.label}
                                </MenuItem>
                            ))}
                        </FastField>
                    </Grid>
                );
            })}
        </Grid>
    );
});

const LegalObject = (legalObj?: any) => {
    return {
        name: '',
        company_type: 'limited',
        passport: '',
        national: '',
        birth: '',
        ...legalObj,
    };
};

const LegalForm = withFormik({
    mapPropsToValues: () => LegalObject(),
    validationSchema: LegalSchema,
    validateOnChange: false,
    validateOnBlur: true,
    handleSubmit: (values, { setSubmitting }) => {},
})(LegalFormTemplate);

export { LegalSchema, LegalForm, LegalFormTemplate, LegalFields, LegalObject };
